import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock, Check, X, AlertCircle, Calendar, Lock, RefreshCw, Download } from 'lucide-react';
import { withdrawalApi, settingsApi, userApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function UserWithdraw() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalInfo, setWithdrawalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);
  const [formData, setFormData] = useState({
    amount: '',
    wallet_address: user?.wallet_address || ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, withdrawalsRes, infoRes] = await Promise.all([
        settingsApi.get(),
        withdrawalApi.getAll(),
        userApi.getWithdrawalInfo()
      ]);
      setSettings(settingsRes.data.settings);
      setWithdrawals(withdrawalsRes.data.withdrawals);
      setWithdrawalInfo(infoRes.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReinvest = async (investmentId) => {
    try {
      await userApi.reinvestCapital(investmentId);
      toast.success('Capital reinvested successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reinvest');
    }
  };

  const today = new Date().getDate();
  const withdrawalDates = settings?.withdrawal_dates || [];
  const isWithdrawalDay = withdrawalInfo?.is_withdrawal_day ?? (withdrawalDates.length === 0 || withdrawalDates.includes(today));
  const nextWithdrawalDay = withdrawalDates.find(d => d > today) || withdrawalDates[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isWithdrawalDay) {
      toast.error(`Withdrawals are only available on specific dates. Next: ${nextWithdrawalDay}th`);
      return;
    }

    const amount = parseFloat(formData.amount);
    if (amount < (settings?.min_withdrawal || 10)) {
      toast.error(`Minimum withdrawal is $${settings?.min_withdrawal || 10}`);
      return;
    }

    if (amount > (settings?.max_withdrawal || 10000)) {
      toast.error(`Maximum withdrawal is $${settings?.max_withdrawal || 10000}`);
      return;
    }

    if (amount > user?.balance) {
      toast.error('Insufficient balance');
      return;
    }

    setSubmitting(true);
    try {
      const response = await withdrawalApi.create({
        amount: amount,
        wallet_address: formData.wallet_address
      });
      toast.success(`Withdrawal submitted! Fee: $${response.data.fee.toFixed(2)}`);
      setFormData({ ...formData, amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const feePercent = settings?.withdrawal_fee || 5;
  const feeAmount = formData.amount ? parseFloat(formData.amount) * (feePercent / 100) : 0;
  const netAmount = formData.amount ? parseFloat(formData.amount) - feeAmount : 0;
  const usdToBLC = (usd) => blcPrice > 0 ? (usd / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="withdraw-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-blue-500/10">
          <ArrowUpRight className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Withdraw Funds</h1>
          <p className="text-sm text-zinc-500">Request a withdrawal to your wallet</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Balance & Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="glass-card p-6">
            <p className="text-xs text-zinc-500 uppercase mb-2">Available Balance</p>
            <p className="text-3xl text-white font-medium">${user?.balance?.toFixed(2) || '0.00'}</p>
            <p className="text-sm text-amber-400 mt-1">{usdToBLC(user?.balance || 0)} BLC <span className="text-zinc-600 text-xs">@ ${blcPrice}/BLC</span></p>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-white font-medium mb-3">Withdrawal Rules</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Minimum Withdrawal</span>
                <span className="text-white">${settings?.min_withdrawal || 10}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Maximum Withdrawal</span>
                <span className="text-white">${settings?.max_withdrawal || 10000}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Withdrawal Fee</span>
                <span className="text-amber-400">{feePercent}%</span>
              </div>
            </div>
          </div>

          {/* Withdrawal Dates */}
          <div className={`glass-card p-6 ${isWithdrawalDay ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className={`w-4 h-4 ${isWithdrawalDay ? 'text-green-400' : 'text-red-400'}`} />
              <h3 className="text-white font-medium">Withdrawal Schedule</h3>
            </div>
            {isWithdrawalDay ? (
              <p className="text-sm text-green-400">Withdrawals are available today!</p>
            ) : (
              <div>
                <p className="text-sm text-red-400 mb-2">Withdrawals not available today</p>
                <p className="text-xs text-zinc-400">
                  Available dates: {withdrawalDates.join(', ') || 'Any day'}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Next withdrawal day: {nextWithdrawalDay}th of this month
                </p>
              </div>
            )}
            
            {/* Daily/Weekly Limits */}
            {withdrawalInfo && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-zinc-400 mb-2">Withdrawal Limits</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Daily Remaining</span>
                    <span className="text-white">${withdrawalInfo.daily_remaining?.toFixed(2)} / ${withdrawalInfo.daily_limit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Weekly Remaining</span>
                    <span className="text-white">${withdrawalInfo.weekly_remaining?.toFixed(2)} / ${withdrawalInfo.weekly_limit}</span>
                  </div>
                  {withdrawalInfo.allowed_days && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Allowed Days</span>
                      <span className="text-amber-400">{withdrawalInfo.allowed_days.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Locked Capital */}
          {withdrawalInfo?.locked_capital?.length > 0 && (
            <div className="glass-card p-6 border-l-4 border-amber-500">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-amber-400" />
                <h3 className="text-white font-medium">Locked Capital</h3>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Capital from expired packages is locked for 30 days. You can reinvest immediately or wait to withdraw.
              </p>
              <div className="space-y-3">
                {withdrawalInfo.locked_capital.map((lc) => (
                  <div key={lc.investment_id} className="bg-black/30 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">${lc.amount.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500">Unlocks: {lc.release_date?.slice(0, 10)}</p>
                    </div>
                    <Button 
                      onClick={() => handleReinvest(lc.investment_id)}
                      size="sm"
                      className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Reinvest
                    </Button>
                  </div>
                ))}
                <p className="text-xs text-zinc-500 text-right">
                  Total Locked: <span className="text-amber-400">${withdrawalInfo.total_locked?.toFixed(2)}</span>
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Withdrawal Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6"
        >
          <h2 className="text-white font-medium mb-4">Request Withdrawal</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Amount (USDT)</label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="Enter amount in USDT"
                className="bg-black/50 border-white/10 text-white"
                data-testid="withdraw-amount"
                disabled={!isWithdrawalDay}
                required
              />
              {formData.amount && parseFloat(formData.amount) > 0 && (
                <p className="text-xs text-amber-400 mt-1.5">
                  <span className="text-zinc-500">Equivalent:</span> {usdToBLC(parseFloat(formData.amount))} BLC
                </p>
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Wallet Address (BEP-20)</label>
              <Input
                type="text"
                value={formData.wallet_address}
                onChange={(e) => setFormData({...formData, wallet_address: e.target.value})}
                placeholder="0x..."
                className="bg-black/50 border-white/10 text-white font-mono"
                data-testid="withdraw-wallet"
                disabled={!isWithdrawalDay}
                required
              />
            </div>

            {formData.amount && (
              <div className="p-4 rounded-lg bg-black/30 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Amount</span>
                  <div className="text-right">
                    <span className="text-white">${parseFloat(formData.amount).toFixed(2)}</span>
                    <span className="text-amber-400 text-xs ml-1">({usdToBLC(parseFloat(formData.amount))} BLC)</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Fee ({feePercent}%)</span>
                  <span className="text-red-400">-${feeAmount.toFixed(2)}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400">You'll Receive</span>
                  <div className="text-right">
                    <span className="text-green-400 font-medium">${netAmount.toFixed(2)}</span>
                    <span className="text-green-400/70 text-xs ml-1">({usdToBLC(netAmount)} BLC)</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-zinc-400">BLC Rate</span>
                  <span className="text-amber-400">${blcPrice} per BLC</span>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !isWithdrawalDay}
              className="w-full gold-btn"
              data-testid="submit-withdraw"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : !isWithdrawalDay ? (
                'Withdrawals Unavailable Today'
              ) : (
                'Submit Withdrawal'
              )}
            </Button>
          </form>
        </motion.div>
      </div>

      {/* Withdrawal History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h2 className="text-white font-medium mb-4">Withdrawal History</h2>
        
        {withdrawals.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No withdrawals yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Fee</th>
                  <th>Net Amount</th>
                  <th>Wallet</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id}>
                    <td className="text-zinc-400">
                      {new Date(w.created_at).toLocaleDateString()}
                    </td>
                    <td className="text-white font-medium">
                      ${w.amount.toFixed(2)}
                      <span className="text-amber-400 text-xs ml-1">({usdToBLC(w.amount)} BLC)</span>
                    </td>
                    <td className="text-red-400">-${w.fee.toFixed(2)}</td>
                    <td className="text-green-400">
                      ${w.net_amount.toFixed(2)}
                      <span className="text-green-400/60 text-xs ml-1">({usdToBLC(w.net_amount)} BLC)</span>
                    </td>
                    <td className="text-zinc-400 font-mono text-xs">
                      {w.wallet_address.slice(0, 10)}...
                    </td>
                    <td>
                      <span className={`badge ${
                        w.status === 'approved' ? 'badge-success' :
                        w.status === 'rejected' ? 'badge-danger' : 'badge-warning'
                      }`}>
                        {w.status === 'approved' && <Check className="w-3 h-3 mr-1" />}
                        {w.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                        {w.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {w.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
